import api from './api';
import { cachedGet } from '../utils/lookupCache';
import { searchLocations } from './locations.service';
import type { QuoteListRow, QuoteDetailData, QuoteCreatePayload, QuoteCreateResult, QuoteUpdatePayload, BillToLookupRow, CustomerLookupRow, CustomerFormOptions, CustomerCreatePayload, CustomerCreateResult, LocationLookupRow, SalesforceLookupRow, GroupedSearchResult } from '../pages/order-management/fixtures';

export type SearchBy = 'quote_number' | 'customer' | 'bill_to' | 'lane' | 'csr' | 'po'
  | 'sage_tag' | 'contact_name' | 'delivery_city' | 'delivery_zip';
export type RevisionsFilter = 'current' | 'all' | 'original';
export type QuoteTypeFilter = 'ALL' | 'WAREHOUSE' | 'DIRECT';
export type ViewMode = 'delivery' | 'billing' | 'costing' | 'lane_detail';
export type StatusFilter = 'ALL' | 'SECURED' | 'UNSECURED' | 'EXPIRED';

/** Recipient options shared by single send (POST /{q}/{r}/send) and bulk send
 *  (POST /send-multi) — V1's View/Send Quote dialog checkboxes. */
export interface SendQuoteRecipients {
  sendToUser?: boolean;
  sendToCsr?: boolean;
  sendToPricing?: boolean;
  sendToCustomer?: boolean;
  sendToOther?: boolean;
  otherEmail?: string;
}

/** Quote field/action ACL keys (P18 #46) — GET /quotes/acls returns the subset
 *  the current user holds. A key absent from the set means the gate is active
 *  AND the user is not permitted; while dormant (no group configured) every key
 *  is returned, so the UI behaves exactly as before. */
export type QuoteAclKey =
  | 'costing' | 'dupe' | 'newrevision' | 'bypassminimumrate'
  | 'viewtariff' | 'edittariff' | 'intelligenceinfo' | 'history_unmask';

/** One row in the PRO Quote # picker — shared by the typeahead and the spyglass
 *  lookup, so both render through the same detail card. */
export interface QuotePickerRow {
  quoteNumber: number;
  revisionNumber: number;
  /** "NNNNNN-R" label as V1 shows it (quoterevisions.quoterevision). */
  quoteRevision: string;
  quoteType: string;
  billTo: string;
  customerName: string;
  moveType: string;
  rateZone: string;
  city: string;
  state: string;
  equipSize: string;
  equipType: string;
  /** Lane dray rate as a plain decimal string (directquote/transloadquote
   *  drayrate); '' when the revision has no lane row. */
  drayRate: string;
  /** MM/DD/YYYY, or null when the revision has no end date. */
  endDate: string | null;
  expired: boolean;
}

/** Result of the spyglass lookup. `rows` empty + `message` set means the PRO
 *  can't be matched (e.g. it resolves to a discontinued WAREHOUSE quote). */
/**
 * The open form's quote-match context — V1's `quotevars` (js/cms.js:2950-2998),
 * minus the two fields our DIRECT-arm port never reads (billfromsystem /
 * donotbillreason, which V1 used only for the discontinued warehouse arm).
 * Built by useOrderEditing.quoteLookupContext from the edited-overlaid order +
 * effective legs. Every field overrides its saved counterpart server-side, and
 * '' / 0 is a real value meaning "the form says blank".
 */
export interface QuoteLookupContext {
  movetype: string;
  ratezone: string;
  billto: number;
  customercode: number;
  otr: string;
  /** Delivery-leg destination location recno, as a string ('0' when unset). */
  deliverycode: string;
  /** TRANSLOAD / ADD PRODUCT / REMOVE PRODUCT leg destination recno. */
  transloadcode: string;
  /** Which document to match for — 0/absent is the main invoice, higher its
   *  accessorial. Only the saved-row bill-to is scoped by it: `billto` lives on the
   *  invoice and an accessorial is often billed to another party, while everything
   *  else above is order-level and shared. */
  accessorialnum?: number;
}

export interface QuotePickerLookupResult {
  /** V1's banner: "BILL TO: x - ZIP/WHSE: y - RATE ZONE: z". */
  searchCriteria: string;
  /** 'exact' = matched the delivery location or customer; 'zip' = looser ZIP-only
   *  fallback tier; 'none' = nothing matched. */
  tier: 'exact' | 'zip' | 'none';
  message: string;
  rows: QuotePickerRow[];
}

export interface SearchQuotesParams {
  searchFor?: string;
  searchBy?: SearchBy;
  revisions?: RevisionsFilter;
  /** With revisions 'current' | 'original', ALSO include revisions whose
   *  effectivedate is after today (queued, not yet current). No-op with 'all'. */
  includeFuture?: boolean;
  showEnded?: boolean;
  quoteType?: QuoteTypeFilter;
  view?: ViewMode;
  effectiveFrom?: string; // ISO date YYYY-MM-DD
  effectiveTo?: string;
  status?: StatusFilter;
  /** Custom filter: JSON FilterCondition[] (or legacy blob). ANDed with customSearch. */
  customFilter?: string;
  /** Custom search: one-field multi-value blob (field~~=~~v1,v2~~). ANDed with customFilter. */
  customSearch?: string;
  /** P13 N47: server-side sort spec, `col:dir,col:dir` (allowlisted columns). */
  orderBy?: string;
  /** Flat-view dynamic columns: comma-delimited extra scope='quotes' field names
   *  to add as columns (e.g. 'quote_commodity,quote_hazmat'). They ride along in
   *  the grouped query's lanes (which feed the flat grid). */
  fields?: string;
  page?: number;
  pageSize?: number;
}

/** Grouped-display search params: every flat filter/sort (except row paging),
 *  plus group-level paging. Mode is implied (group_by=customer_billto). */
export interface GroupedSearchParams extends Omit<SearchQuotesParams, 'page' | 'pageSize'> {
  groupPage?: number;
  groupSize?: number;
  /** Load EVERY matching group in one request (sends group_all=true) so the
   *  grid can sort/filter/search/page client-side. When set, group paging
   *  (groupPage/groupSize) is ignored; the backend echoes groupPage=1 and
   *  groupSize=totalGroups. */
  all?: boolean;
}

/** A saved custom filter (quote.custom_filters). */
export interface SavedCustomFilter {
  recno: number;
  name: string;
  filter: string;
}

/** A published tariff rate (additionalcharges/handlingitems where tariff='Y'). */
export interface PublishedTariffRate {
  source: string;
  description: string;
  ratetype: string;
  defaultRate: number;
  minimumRate: number;
}

export interface SearchQuotesResponse {
  total: number;
  page: number;
  pageSize: number;
  rows: QuoteListRow[];
}

/** One row from GET /quotes/{quoteNumber}/history. */
export interface QuoteHistoryRow {
  recno: number;
  table: string;
  key: string;
  datetime: string;
  user: string;
  column: string;
  oldVal: string;
  newVal: string;
  type: string;
}

/** GET /quotes/{quoteNumber}/revisions — every revision of a quote, oldest
 *  first, each the same shape as a single-revision detail read. */
/** Result of POST /quotes/salesforce/import. */
export interface SalesforceImportResult {
  raw: number;
  created: number;
  updated: number;
  unchanged: number;
}

/** One event type from GET /quotes/event-history (grouped summary). */
export interface EventHistorySummaryRow {
  type: string;
  lastDate: string;
  user: string;
  lastResult: string;
  count: number;
}

/** One row from GET /quotes/event-history?type=… (per-type drill-down). */
export interface EventHistoryDetailRow {
  datetime: string;
  user: string;
  file: string;
  result: string;
}

export interface QuoteRevisionsResponse {
  quoteNumber: number;
  quoteType: string;
  /** True count of revisions on the quote. `revisions` may be a most-recent
   *  window of this (large-history quotes are capped for responsiveness). */
  totalRevisions: number;
  revisions: QuoteDetailData[];
}

/** Result of POST /quotes/{n}/migration-verify — the new v2-migration verified state. */
export interface MigrationVerifyResult {
  quoteNumber: number;
  v2MigrationTracked: boolean;
  v2MigrationVerified: boolean;
  v2MigrationVerifiedBy: string | null;
  v2MigrationVerifiedDate: string | null;
}

export type ChargeCatalogScope = 'direct' | 'transload' | 'delivery';

/** One row from GET /quotes/charge-catalog — quote.additionalcharges joined
 *  to its current effective additionalchargerates row. */
export interface ChargeCatalogRow {
  recno: number;
  description: string;
  region: string;
  rateType: string;
  pdfMessage: string;
  tariff: boolean;
  /** Decimal strings ("" when no effective rate row exists). */
  minimumRate: string;
  defaultRate: string;
}

/** One row from GET /quotes/handling-catalog (quote.handlingitems). */
export interface HandlingCatalogRow {
  recno: number;
  description: string;
  pdfMessage: string;
  rateType: string;
  unitType: string;
  tariff: boolean;
  defaultRate: string;
  minimumRate: string;
}

/** One row from GET /quotes/ratezones (cms.ratezones). */
export interface RateZoneRow {
  rateZone: string;
  displayName: string;
  region: string;
}

/** Payload for POST /quotes/{q}/revisions (V1 "New Revision"). Mirrors the
 *  backend RevisionCreateIn model. Copy flags default to true server-side
 *  except notes/documents (V1 requires explicit opt-in for those). */
export interface RevisionCreatePayload {
  sourceRevisionNumber?: number;
  businesstype: string;
  effectivedate?: string;
  enddate?: string;
  copyCustomer?: boolean;
  /** N5: dupe-only — copy the Salesforce Opportunity id (off clears it). */
  copySalesforceOpp?: boolean;
  copyDray?: boolean;
  copyWarehouse?: boolean;
  copyLanes?: boolean;
  copyHandling?: boolean;
  copyCharges?: boolean;
  copyProductRates?: boolean;
  copyStopoffs?: boolean;
  copyNotes?: boolean;
  copyDocuments?: boolean;
}

/** One existing quote that matches a new draft's bill-to + type + lane
 *  (GET /quotes/dupe-check — V1 checkfordupes). */
export interface DupeCandidate {
  quoteNumber: number;
  revisionNumber: number;
  billtoname: string;
  /** Human-readable lane summary (ratezone / zone / zip|city·state). */
  lane: string;
}

/** One selectable EXTRA-IN-RATE combo (N39): a subset of a scope's
 *  extra-in-rate charges the PDF can print as a "base + combo" alternative. */
export interface ExtraInRateCombo {
  recnolist: string;
  descriptions: string[];
  total: number;
  checked: boolean;
}

/** EXTRA-IN-RATE combos grouped by scope (transload/direct dray, or a lane). */
export interface ExtraInRateScope {
  tablename: string;
  tablerecno: number;
  combos: ExtraInRateCombo[];
}

/** One warehouse customer + its product codes (GET /quotes/whse-customer-lookup, N60). */
export interface WhseCustomerRow {
  customercode: number;
  custname: string;
  city: string;
  state: string;
  productCodes: Array<{ recno: number; sku: string; masterUom: string }>;
}

/** Query for GET /quotes/dupe-check. Send the lane keys for the draft's
 *  quote type (DIRECT: ratezone/deliveryzone + zip|city/state). */
export interface DupeCheckParams {
  quotetype: string;
  billto?: number;
  billtoname?: string;
  ratezone?: string;
  deliveryzone?: string;
  delivertozip?: string;
  delivertocity?: string;
  delivertostate?: string;
  movetype?: string;
  whsetype?: string;
  packagetype?: string;
  warehouse?: string;
  size?: string;
  equiptype?: string;
}

/** GET /quotes/geocode-citystatezip — resolved delivery location. */
export interface GeoCityStateZip {
  city: string;
  state: string;
  zip: string;
}

/** One driving leg of the round-trip mileage breakdown (V1 getmiles_Google). */
export interface QuoteMilesLeg {
  /** Google-resolved origin address (e.g. "Shoreacres, TX 77571"). */
  origin: string;
  /** Google-resolved destination address. */
  destination: string;
  /** This leg's driving miles (2dp). */
  miles: number;
  /** Cumulative miles through this leg. */
  total: number;
}

/** GET /quotes/calculate-miles — delivery mileage result. */
export interface QuoteMilesResult {
  /** Driving miles (2dp) — the round-trip total, or null when uncomputable. */
  miles: number | null;
  /** Origin ZIP resolved from the rate zone (informational; may be 2-leg). */
  originZip: string;
  /** Per-leg round-trip breakdown for the "Show Miles" modal (empty on error). */
  legs: QuoteMilesLeg[];
  /** Why miles is null (no origin ZIP for the zone, no route, etc.). */
  error: string | null;
}

/** GET /quotes/dray-rate — the DIRECT-quote dray rate (V1 updatedirectdelivery):
 *  deliverystandards (zone,zip) standard rate, or a ratezonemileage band, or a
 *  "not defined" signal for the modal. */
export interface DrayRate {
  /** The dray rate to auto-fill (standard rate or mileage band), or null. */
  drayRate: number | null;
  /** deliverystandards.standardrate (or a FLAT mileage band) — for the "Standard:" display. */
  standardRate: number | null;
  /** deliverystandards.minimumrate — for the "Minimum:" display (standards zones). */
  minimumRate: number | null;
  /** ratezonemileage per-mile rate — for the "Per Mile Standard:" display. */
  perMileStandard: number | null;
  /** Mileage band type: 'PER MILE' | 'FLAT' | null. */
  ratetype: string | null;
  /** 'deliverystandards' | 'ratezonemileage' | null. */
  source: string | null;
  /** True for a LOCAL standards zone with no (zone,zip) pairing — show the modal. */
  notDefined: boolean;
  /** The modal message when notDefined. */
  message: string | null;
}

/** GET /quotes/fsc — DIRECT-quote fuel-surcharge resolution (V1 updatefuelsurcharge). */
export interface FscResult {
  /** The FSC rate to fill for CURRENT / CUSTOMER; null for user-entered / FLAT / blank. */
  fscRate: number | null;
  /** Whether the FSC rate field is read-only (CURRENT / CUSTOMER / FLAT / blank). */
  readOnly: boolean;
  /** CUSTOMER tariff rate type: 'STANDARD' | 'PER MILE' | 'PER GALLON' | 'FLAT' | null. */
  ratetype: string | null;
  /** Per-dray FSC $ added for the estimated revenue: (dray + adjustment) × volume. */
  adjustment: number;
}

/** A row in the customer FSC tariff Lookup picker (GET /quotes/fsc-tariffs). */
export interface FscTariff {
  tariffNumber: number;
  tariffType: string;
  fuelIndex: string;
  ratetype: string;
  otrLocal: string;
  effectiveDate: string;
  expirationDate: string;
  currentRate: number | null;
  /** Free-text note on the tariff revision — shown via a hover comment icon. */
  comments: string;
  /** Free-text Name/Description of the tariff (as-typed) — shown next to Tariff #. */
  name: string;
}

/** GET /quotes/rate-standards — the V1 standards/minimum-rate lookups. */
export interface RateStandards {
  dray: Array<{ warehouse: string; rateZone: string; standardRate: string; minimumRate: string }>;
  delivery: Array<{ rateZone: string; city: string; subsection: string; state: string; zip: string; standardRate: string; minimumRate: string }>;
  transload: Array<{ whseType: string; packageType: string; moveType: string; size20StandardRate: string; size20MinimumRate: string; size40StandardRate: string; size40MinimumRate: string }>;
  mileage: Array<{ rateZone: string; miles: number; rateType: string; mileageRate: string }>;
}

/** Product-rate fields for POST/PATCH /quotes/{q}/{r}/product-rates. */
export interface ProductRateFieldsPayload {
  whsecustomercode?: number;
  productcoderecno?: number;
  handlingrate?: number;
  handlingtype?: string;
  storagerate?: number;
  storagetype?: string;
}

/** Stopoff fields for POST/PATCH /quotes/{q}/{r}/stopoffs (quote.stopoffs). */
export interface StopoffFieldsPayload {
  stopnum?: number;
  stopcode?: number;
  stopname?: string;
  stopadd1?: string;
  stopadd2?: string;
  stopcity?: string;
  stopstate?: string;
  stopzip?: string;
  stoptype?: string;
}

/** Delivery-lane fields for POST/PATCH /quotes/{q}/{r}/lanes. snake_case
 *  mirrors the backend LaneFieldsIn model (deliveryquote del* columns). */
export interface LaneFieldsPayload {
  delwarehouse?: string; delratezone?: string; delequiptype?: string;
  deldelivertocode?: number; deldelivertoname?: string;
  deladdr1?: string; deladdr2?: string; delcity?: string; delstate?: string; delzip?: string;
  delmiles?: number; delcarriercode?: string; delcarrierrate?: number; delcarrierquotenumber?: string;
  deldeliveryrate?: number; deldeliverytype?: string; deldraywithcharges?: number;
  delfscrate?: number; delallfscrate?: number; delfsctype?: string; delfsctariff?: number;
  delindray?: string; deloverloadrate?: number; deloverloadtype?: string;
  delwaitingtimefree?: number; delyardstoragefree?: number;
  delchassisfeetype?: string; delchassisfeedays?: number;
  dellightweightchassisfeetype?: string; dellightweightchassisfeedays?: number;
  delcomments?: string;
}

/** Builds the shared filter/sort query params for GET /quotes/search (snake_case).
 *  Row/group paging is appended by each caller (searchQuotes vs searchQuotesGrouped),
 *  so both modes get the identical filter/sort handling from one place. */
function buildQuoteSearchParams(params: Omit<SearchQuotesParams, 'page' | 'pageSize'>): Record<string, string> {
  const qp: Record<string, string> = {};
  if (params.searchFor)      qp.search_for      = params.searchFor;
  if (params.searchBy)       qp.search_by       = params.searchBy;
  if (params.revisions)      qp.revisions       = params.revisions;
  // Only sent when ON so existing request shapes are untouched (server default false).
  if (params.includeFuture)  qp.include_future  = 'true';
  if (params.showEnded !== undefined) qp.show_ended = String(params.showEnded);
  if (params.quoteType)      qp.quote_type      = params.quoteType;
  if (params.view)           qp.view            = params.view;
  if (params.effectiveFrom)  qp.effective_from  = params.effectiveFrom;
  if (params.effectiveTo)    qp.effective_to    = params.effectiveTo;
  if (params.status)         qp.status          = params.status;
  if (params.customFilter)   qp.custom_filter   = params.customFilter;
  if (params.customSearch)   qp.custom_search   = params.customSearch;
  if (params.orderBy)        qp.order_by        = params.orderBy;
  if (params.fields)         qp.fields          = params.fields;
  return qp;
}

/** Round a decimal display string to 2dp; passes blanks / non-numeric strings
 *  (e.g. an already-formatted "$50,000.00") through unchanged. */
function round2(v: string | null | undefined): string {
  if (v == null || v === '') return (v ?? '') as string;
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : v;
}

/** Normalize the noisy decimal(13,5) money/rate fields a loaded quote carries to
 *  2dp for display (the DB sends e.g. "235.00000"). The FSC rate and Additional
 *  Charges rates are intentionally left at full precision. */
function normalizeQuoteDisplay(d: QuoteDetailData): QuoteDetailData {
  const out: QuoteDetailData = { ...d, estRev: round2(d.estRev), estMargin: round2(d.estMargin) };
  if (d.direct) {
    out.direct = {
      ...d.direct,
      drayRate: round2(d.direct.drayRate),
      drayWithCharges: round2(d.direct.drayWithCharges),
      deliverToMiles: round2(d.direct.deliverToMiles),
    };
  }
  return out;
}

/** Full name + address for a bill-to or customer (Quotes grid hover tooltip). */
export interface PartyAddress {
  name: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip: string;
  sageTag: string;
}

const quoteService = {
  /**
   * Calls GET /api/v1/quotes/search. The backend mirrors V1's quote-master
   * UNION-over-WAREHOUSE/DIRECT search and derives a status pill from
   * quoterevisions.status + enddate.
   */
  async searchQuotes(params: SearchQuotesParams = {}): Promise<SearchQuotesResponse> {
    const qp = buildQuoteSearchParams(params);
    if (params.page)           qp.page            = String(params.page);
    if (params.pageSize)       qp.page_size       = String(params.pageSize);

    const response = await api.get('/quotes/search', { params: qp });
    return response.data as SearchQuotesResponse;
  },

  /**
   * GET /api/v1/quotes/search?group_by=customer_billto — the grouped-display
   * mode. Same endpoint and same filter/sort params as searchQuotes(), but
   * paged by GROUP (group_page/group_size) instead of by row. Each lane in a
   * group is the exact QuoteListRow the flat grid returns.
   *
   * Pass `{ all: true }` to load EVERY matching group in one request (sends
   * group_all=true, skips group paging) so the grid can sort/filter/search/page
   * client-side. The response is the same GroupedSearchResult shape, with
   * groupPage=1 and groupSize=totalGroups.
   */
  async searchQuotesGrouped(params: GroupedSearchParams = {}): Promise<GroupedSearchResult> {
    const qp = buildQuoteSearchParams(params);
    qp.group_by = 'customer_billto';
    if (params.all) {
      qp.group_all = 'true';
    } else {
      if (params.groupPage) qp.group_page = String(params.groupPage);
      if (params.groupSize) qp.group_size = String(params.groupSize);
    }

    const response = await api.get('/quotes/search', { params: qp });
    return response.data as GroupedSearchResult;
  },

  /** GET /api/v1/quotes/custom-filters — the current user's saved custom filters. */
  async listCustomFilters(): Promise<SavedCustomFilter[]> {
    const response = await api.get('/quotes/custom-filters');
    return response.data as SavedCustomFilter[];
  },

  /** POST /api/v1/quotes/custom-filters — upsert a named custom filter (dev-gated). */
  async saveCustomFilter(name: string, filter: string): Promise<{ recno: number }> {
    const response = await api.post('/quotes/custom-filters', { name, filter });
    return response.data as { recno: number };
  },

  /** GET /api/v1/quotes/tariff-rates — read-only published tariff rates (N61),
   *  scoped by quote type (DIRECT = direct-scoped charges; WAREHOUSE adds
   *  transload/delivery + handling). */
  async listTariffRates(quotetype = 'DIRECT'): Promise<PublishedTariffRate[]> {
    const response = await api.get('/quotes/tariff-rates', { params: { quotetype } });
    return response.data as PublishedTariffRate[];
  },

  /**
   * GET /api/v1/quotes/{quoteNumber}/{revisionNumber}. Returns the Quote
   * Details header + Customer Info tab fields. Throws on non-2xx (axios
   * default); callers should catch and surface a 404 as "not found".
   */
  async getQuote(quoteNumber: number, revisionNumber: number): Promise<QuoteDetailData> {
    const response = await api.get(`/quotes/${quoteNumber}/${revisionNumber}`);
    return normalizeQuoteDisplay(response.data as QuoteDetailData);
  },

  /**
   * POST /api/v1/quotes/{quoteNumber}/migration-verify. Marks a v2-migrated quote
   * as verified (a human reviewed the migrated data in 360) or, with
   * verified=false, un-verifies it. Returns the new verification state.
   */
  async verifyMigration(quoteNumber: number, verified: boolean): Promise<MigrationVerifyResult> {
    const response = await api.post(`/quotes/${quoteNumber}/migration-verify`, { verified });
    return response.data as MigrationVerifyResult;
  },

  /**
   * GET /api/v1/quotes/{quoteNumber}/history. Returns the full edit-history
   * log for a quote across all revisions, newest-first.
   */
  async getQuoteHistory(quoteNumber: number, mineToday = false): Promise<QuoteHistoryRow[]> {
    const response = await api.get(`/quotes/${quoteNumber}/history`, {
      params: mineToday ? { mine_today: true } : {},
    });
    return response.data as QuoteHistoryRow[];
  },

  /**
   * GET /api/v1/quotes/{quoteNumber}/revisions. Returns every revision of the
   * quote (oldest-first), each as the same QuoteDetailData shape getQuote
   * returns. Feeds the Revision History compare view (V1 viewrevisionhistory).
   */
  async getQuoteRevisions(quoteNumber: number): Promise<QuoteRevisionsResponse> {
    const response = await api.get(`/quotes/${quoteNumber}/revisions`);
    return response.data as QuoteRevisionsResponse;
  },

  /**
   * POST /api/v1/quotes. Creates a new DIRECT quote (header + revision +
   * directquote + optional tariff rates and notes) and returns the assigned
   * quote/revision numbers.
   */
  async createQuote(payload: QuoteCreatePayload): Promise<QuoteCreateResult> {
    const response = await api.post('/quotes', payload);
    return response.data as QuoteCreateResult;
  },

  /**
   * PATCH /api/v1/quotes/{quoteNumber}/{revisionNumber}. Edits an existing
   * DIRECT quote (header / revision / directquote fields). Send only the
   * changed fields; `payload.expectedLastChanged` (the raw lastChangedToken
   * from getQuote) drives the optimistic-concurrency check — a 409 means the
   * row changed since it was loaded. Returns the quote/revision numbers.
   */
  async updateQuote(quoteNumber: number, revisionNumber: number, payload: QuoteUpdatePayload): Promise<QuoteCreateResult> {
    const response = await api.patch(`/quotes/${quoteNumber}/${revisionNumber}`, payload);
    return response.data as QuoteCreateResult;
  },

  /**
   * GET /cms-lookups/csrs — the active CSR assignment roster (V1 buildcsr_data):
   * DISPATCH_CSR/MANAGER users plus WAREHOUSE-group users with a CSR number.
   * Returns {value: username, label: "NAME (csrnumber)"} for the CSR typeahead.
   */
  async getCsrOptions(): Promise<{ value: string; label: string }[]> {
    // Shared TTL cache: the roster is also fetched by OrderFormContext on app
    // boot — one request serves both (see utils/lookupCache).
    const response = await cachedGet<{ value: string; label: string }[]>('/cms-lookups/csrs');
    return response.data;
  },

  /**
   * GET /api/v1/quotes/billto-lookup?q=... Returns matching bill-to records
   * (code, name, address, sales CSR, credit info) for the create wizard.
   */
  async lookupBillTo(q: string): Promise<BillToLookupRow[]> {
    const response = await api.get('/quotes/billto-lookup', { params: { q } });
    return response.data as BillToLookupRow[];
  },

  /** Full name + address for a bill-to (recno) or customer (code) — the Quotes
   *  grid hover tooltip. billto → cms.billtos, customer → cms.customers. */
  async partyAddress(kind: 'billto' | 'customer', id: number): Promise<PartyAddress> {
    const response = await api.get('/quotes/party-address', { params: { kind, id } });
    return response.data as PartyAddress;
  },

  /**
   * GET /api/v1/quotes/customer-lookup?q=... Matching cms.customers rows for
   * the create wizard's customer picker. masters=true restricts to
   * custtype='MASTER' (the New Customer modal's master-customer picker).
   */
  async lookupCustomer(q: string, masters = false): Promise<CustomerLookupRow[]> {
    const response = await api.get('/quotes/customer-lookup', {
      params: masters ? { q, masters: true } : { q },
    });
    return response.data as CustomerLookupRow[];
  },

  /** GET /api/v1/quotes/customer-form-options — New Customer modal dropdowns. */
  async customerFormOptions(): Promise<CustomerFormOptions> {
    const response = await api.get('/quotes/customer-form-options');
    return response.data as CustomerFormOptions;
  },

  /** POST /api/v1/quotes/customers — create a cms.customers row ("Add As New
   *  Customer" in the create wizard). */
  async createCustomer(payload: CustomerCreatePayload): Promise<CustomerCreateResult> {
    const response = await api.post('/quotes/customers', payload);
    return response.data as CustomerCreateResult;
  },

  /** GET /api/v1/quotes/whse-customer-lookup — warehouse-customer name search
   *  returning each customer's product codes inline, for one-step product-rate
   *  entry (N60). */
  async lookupWhseCustomer(q: string): Promise<WhseCustomerRow[]> {
    const response = await api.get('/quotes/whse-customer-lookup', { params: { q } });
    return response.data as WhseCustomerRow[];
  },

  /**
   * Matching cms.locations rows for the create wizard's delivery-location
   * picker (and the Change Template / tablemaint FK pickers). Rides the
   * shared GET /admin/locations?q=&match=name lookup — same name-first
   * semantics as the former /quotes/location-lookup (lname contains, cyalias
   * exact, recno when all-digits). Lookup only — no add-as-new (the
   * locations page owns creation).
   */
  async lookupLocation(q: string, includeAddress = false): Promise<LocationLookupRow[]> {
    // includeAddress: also match the street-address lines (ladd/ladd2). Used
    // by the EDI location-mapping picker to disambiguate same-name locations
    // by address — name-first pickers stay undiluted by street hits.
    const locs = await searchLocations(q, {
      match: 'name',
      address: includeAddress,
      limit: 20,
    });
    const s = (v: string | null | undefined) => (v ?? '').trim();
    return locs.map((l) => ({
      locationcode: l.recno,
      lname: s(l.lname),
      address1: s(l.ladd),
      address2: s(l.ladd2),
      city: s(l.lcity),
      state: s(l.lstate),
      zip: s(l.lzip),
      cyalias: s(l.cyalias),
      locationtype: s(l.locationtype),
    }));
  },

  /**
   * GET /api/v1/quotes/geocode-citystatezip?q=… — resolve a delivery ZIP to
   * city/state, or 'city, state' to a ZIP (Google Geocoding). V1 equivalent is
   * inline_quote.pl getcitystatezip. Returns blank strings on no match.
   */
  async geocodeCityStateZip(q: string): Promise<GeoCityStateZip> {
    const response = await api.get('/quotes/geocode-citystatezip', { params: { q } });
    return response.data as GeoCityStateZip;
  },

  /**
   * GET /api/v1/quotes/calculate-miles — driving miles from the rate-zone
   * origin ZIP (LOCAL/OTR map) to the delivery ZIP (or city/state). V1
   * equivalent is inline_quote.pl calcmiles. `miles` is null with an `error`
   * when the zone has no origin ZIP or no route is found.
   */
  async calculateQuoteMiles(params: {
    ratezone: string;
    deliveryzone: string;
    zip?: string;
    city?: string;
    state?: string;
  }): Promise<QuoteMilesResult> {
    const response = await api.get('/quotes/calculate-miles', { params });
    return response.data as QuoteMilesResult;
  },

  /**
   * GET /api/v1/quotes/dray-rate?ratezone=…&zip=…&deliveryzone=…&miles=… — the
   * DIRECT-quote dray rate (V1 updatedirectdelivery): a deliverystandards
   * (zone,zip) standard rate, or a ratezonemileage band (FLAT / PER MILE), or a
   * notDefined signal (LOCAL standards zone with no pairing → modal). Drives the
   * Dray Rate auto-fill + the Standard/Minimum reference + the "not defined" modal.
   */
  async getDrayRate(params: { ratezone: string; zip?: string; deliveryzone?: string; miles?: number }): Promise<DrayRate> {
    const response = await api.get('/quotes/dray-rate', { params });
    return response.data as DrayRate;
  },

  /**
   * GET /api/v1/quotes/fsc — resolve the DIRECT-quote fuel surcharge (V1
   * updatefuelsurcharge + estimatedrevenue): the FSC rate to fill (CURRENT /
   * CUSTOMER), the read-only flag, the CUSTOMER ratetype, and the per-dray
   * revenue adjustment.
   */
  async getFsc(params: {
    fsctype: string; fsctariff?: number; deliveryzone?: string;
    fscrate?: number; dray?: number; miles?: number;
    /** FSC calculation date (YYYY-MM-DD) per the quote's fuel-calc basis; omit for today. */
    as_of?: string;
    /**
     * 'invoice' resolves with the reconciler's week rules instead of the quote
     * form's. v1 prices the two differently (the invoice anchors the fuel week on
     * Monday and shifts a Monday date back a full week), so the Invoicing tab must
     * ask for the invoice's rules to preview what will actually be billed.
     */
    basis?: 'invoice';
    /** basis=invoice: the invoice's base dray line item (picks the rate column). */
    liitem?: string;
    /** basis=invoice: quote type feeding the reconciler's rate-column pick. */
    quotetype?: string;
    /** basis=invoice: delivery-leg arrival (YYYY-MM-DD); drives drayotr convergence. */
    delivery_date?: string;
  }): Promise<FscResult> {
    const response = await api.get('/quotes/fsc', { params });
    return response.data as FscResult;
  },

  /**
   * GET /api/v1/quotes/fsc-tariffs?billto=… — the bill-to's selectable customer
   * FSC tariffs (+ current rate) for the "Cust Tariff #" Lookup picker.
   *
   * `{ all: true }` instead lists EVERY selectable tariff — the Rate Calculator
   * has no bill-to. Those rows carry `currentRate: null` by design (resolving a
   * rate per row would be 2-3 queries × ~280 tariffs); use `findFscTariff` or
   * `getFsc` to price the one that's picked.
   */
  async listFscTariffs(billto: number, opts?: { all?: boolean }): Promise<FscTariff[]> {
    const params: Record<string, unknown> = { billto };
    if (opts?.all) params.all = true;
    const response = await api.get('/quotes/fsc-tariffs', { params });
    return (response.data as FscTariff[]) || [];
  },

  /**
   * GET /api/v1/quotes/fsc-tariffs?tariffnumber=… — one selectable tariff by
   * number, WITH its current rate, for the "I already know the number" entry
   * path. Null when the number matches no selectable tariff.
   */
  async findFscTariff(tariffnumber: number): Promise<FscTariff | null> {
    const response = await api.get('/quotes/fsc-tariffs', { params: { tariffnumber } });
    const rows = (response.data as FscTariff[]) || [];
    return rows.length ? rows[0] : null;
  },

  /**
   * GET /api/v1/quotes/salesforce-lookup?q=… — non-deleted quote.salesforce
   * accounts matching Name (or recno). Feeds the SF account typeahead; the pick
   * sets masterquote.salesforcename/salesforcerecno.
   */
  async lookupSalesforce(q: string): Promise<SalesforceLookupRow[]> {
    const response = await api.get('/quotes/salesforce-lookup', { params: { q } });
    return response.data as SalesforceLookupRow[];
  },

  /**
   * POST /api/v1/quotes/salesforce/import — multipart CSV upload. Upserts SF
   * accounts into quote.salesforce and returns the run counts. Dev-gated server
   * side (testquote) until the P18 write-gate lifts.
   */
  async importSalesforce(file: File): Promise<SalesforceImportResult> {
    const fd = new FormData();
    fd.append('file', file);
    const response = await api.post('/quotes/salesforce/import', fd);
    return response.data as SalesforceImportResult;
  },

  /**
   * POST /api/v1/quotes/salesforce/export?begin_date=YYYY-MM-DD — returns the
   * SF export CSV as a Blob and stamps salesforceexportdatetime server-side
   * (dev-gated until P18). Save via URL.createObjectURL.
   */
  async exportSalesforce(beginDate: string): Promise<Blob> {
    const response = await api.post('/quotes/salesforce/export', null, {
      params: { begin_date: beginDate },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  /** GET /api/v1/quotes/event-history — systemevent rows grouped by type. */
  async getEventHistory(): Promise<{ events: EventHistorySummaryRow[] }> {
    const response = await api.get('/quotes/event-history');
    return response.data as { events: EventHistorySummaryRow[] };
  },

  /** GET /api/v1/quotes/event-history?type=… — full history for one type. */
  async getEventHistoryDetail(type: string): Promise<{ type: string; history: EventHistoryDetailRow[] }> {
    const response = await api.get('/quotes/event-history', { params: { type } });
    return response.data as { type: string; history: EventHistoryDetailRow[] };
  },

  /**
   * GET /api/v1/quotes/charge-catalog?scope=... The additional-charge catalog
   * with each charge's current effective default/minimum rate. Charge rows on
   * quotes must reference these recnos (quoteadditionalcharges.additionalchargesrecno).
   */
  async chargeCatalog(scope: ChargeCatalogScope = 'direct'): Promise<ChargeCatalogRow[]> {
    const response = await api.get('/quotes/charge-catalog', { params: { scope } });
    return response.data as ChargeCatalogRow[];
  },

  /**
   * GET /api/v1/quotes/dupe-check — existing current-revision quotes matching a
   * new draft's bill-to + quote type + lane (V1 checkfordupes). Empty array =
   * no duplicates; the wizard shows any matches before creating.
   */
  async dupeCheck(params: DupeCheckParams): Promise<DupeCandidate[]> {
    const response = await api.get('/quotes/dupe-check', { params });
    return response.data as DupeCandidate[];
  },

  /** GET /api/v1/quotes/handling-catalog — handlingitems usable on quotes. */
  async handlingCatalog(): Promise<HandlingCatalogRow[]> {
    const response = await api.get('/quotes/handling-catalog');
    return response.data as HandlingCatalogRow[];
  },

  /** GET /api/v1/quotes/ratezones — cms.ratezones lookup. */
  async rateZones(): Promise<RateZoneRow[]> {
    const response = await api.get('/quotes/ratezones');
    return response.data as RateZoneRow[];
  },

  /** POST /quotes/{q}/{r}/charges — add a tariff-rate (charge) row. */
  async addCharge(
    quoteNumber: number,
    revisionNumber: number,
    payload: {
      additionalchargesrecno: number;
      addlchargeratetype?: string;
      addlchargerate?: number;
      freetime?: number;
      freetimeunit?: string;
      additionaltype?: string;
      additionaldays?: number;
      tablename?: 'directquote' | 'transloadquote' | 'deliveryquote';
      tablerecno?: number;
    },
  ): Promise<{ recno: number }> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/charges`, payload);
    return response.data as { recno: number };
  },

  /** PATCH /quotes/{q}/{r}/charges/{recno} — edit a charge's type/rate/free time. */
  async updateCharge(
    quoteNumber: number,
    revisionNumber: number,
    chargeRecno: number,
    payload: { addlchargeratetype?: string; addlchargerate?: number; freetime?: number; freetimeunit?: string; additionaltype?: string; additionaldays?: number },
  ): Promise<{ recno: number }> {
    const response = await api.patch(
      `/quotes/${quoteNumber}/${revisionNumber}/charges/${chargeRecno}`, payload);
    return response.data as { recno: number };
  },

  /** DELETE /quotes/{q}/{r}/charges/{recno} — soft-delete a charge row. */
  async deleteCharge(
    quoteNumber: number,
    revisionNumber: number,
    chargeRecno: number,
  ): Promise<{ recno: number }> {
    const response = await api.delete(`/quotes/${quoteNumber}/${revisionNumber}/charges/${chargeRecno}`);
    return response.data as { recno: number };
  },

  /** POST /quotes/{q}/{r}/notes — append a note (server uppercases + stamps). */
  async addNote(quoteNumber: number, revisionNumber: number, notes: string): Promise<{ recno: number }> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/notes`, { notes });
    return response.data as { recno: number };
  },

  /** POST /quotes/{q}/{r}/lanes — add a delivery lane (WAREHOUSE quotes). */
  async addLane(quoteNumber: number, revisionNumber: number, payload: LaneFieldsPayload): Promise<{ recno: number }> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/lanes`, payload);
    return response.data as { recno: number };
  },

  /** PATCH /quotes/{q}/{r}/lanes/{recno} — edit a delivery lane. */
  async updateLane(quoteNumber: number, revisionNumber: number, laneRecno: number, payload: LaneFieldsPayload): Promise<{ recno: number }> {
    const response = await api.patch(`/quotes/${quoteNumber}/${revisionNumber}/lanes/${laneRecno}`, payload);
    return response.data as { recno: number };
  },

  /** DELETE /quotes/{q}/{r}/lanes/{recno} — soft-delete a delivery lane. */
  async deleteLane(quoteNumber: number, revisionNumber: number, laneRecno: number): Promise<{ recno: number }> {
    const response = await api.delete(`/quotes/${quoteNumber}/${revisionNumber}/lanes/${laneRecno}`);
    return response.data as { recno: number };
  },

  /** POST /quotes/{q}/{r}/handling — add a handlingquote item (WAREHOUSE). */
  async addHandling(
    quoteNumber: number,
    revisionNumber: number,
    payload: {
      handlingitemrecno: number;
      handlingtype?: 'handlingin' | 'handlingout';
      handlingratetype?: string;
      handlingrate?: number;
      handlingunittype?: string;
    },
  ): Promise<{ recno: number }> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/handling`, payload);
    return response.data as { recno: number };
  },

  /** PATCH /quotes/{q}/{r}/handling/{recno} — edit a handling item. */
  async updateHandling(
    quoteNumber: number,
    revisionNumber: number,
    handlingRecno: number,
    payload: { handlingratetype?: string; handlingrate?: number; handlingunittype?: string },
  ): Promise<{ recno: number }> {
    const response = await api.patch(`/quotes/${quoteNumber}/${revisionNumber}/handling/${handlingRecno}`, payload);
    return response.data as { recno: number };
  },

  /** DELETE /quotes/{q}/{r}/handling/{recno} — soft-delete a handling item. */
  async deleteHandling(quoteNumber: number, revisionNumber: number, handlingRecno: number): Promise<{ recno: number }> {
    const response = await api.delete(`/quotes/${quoteNumber}/${revisionNumber}/handling/${handlingRecno}`);
    return response.data as { recno: number };
  },

  /** POST /quotes/{q}/revisions — V1 "New Revision": MAX(rev)+1 with
   *  selective copy. Returns the new revision's id. */
  async createRevision(quoteNumber: number, payload: RevisionCreatePayload): Promise<QuoteCreateResult> {
    const response = await api.post(`/quotes/${quoteNumber}/revisions`, payload);
    return response.data as QuoteCreateResult;
  },

  /** POST /quotes/{q}/{r}/dupe — V1 "Dupe Quote": brand-new quotenumber,
   *  revision 1, selective copy + auto "DUPED FROM" note. */
  async dupeQuote(quoteNumber: number, revisionNumber: number, payload: RevisionCreatePayload): Promise<QuoteCreateResult> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/dupe`, payload);
    return response.data as QuoteCreateResult;
  },

  /** GET /quotes/acls — the field/action ACL keys the current user holds
   *  (P18 #46). Used to hide/disable gated actions. Dormant → all keys. */
  async getQuoteAcls(): Promise<QuoteAclKey[]> {
    const response = await api.get('/quotes/acls');
    return (response.data as { acls: QuoteAclKey[] }).acls;
  },

  /** POST /quotes/{q}/{r}/stopoffs — add a stopoff. Pass laneRecno for a
   *  WAREHOUSE lane stop; omit it for a DIRECT-quote stop. */
  async addStopoff(quoteNumber: number, revisionNumber: number, payload: StopoffFieldsPayload & { laneRecno?: number }): Promise<{ recno: number }> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/stopoffs`, payload);
    return response.data as { recno: number };
  },

  /** PATCH /quotes/{q}/{r}/stopoffs/{recno} — edit a stopoff. */
  async updateStopoff(quoteNumber: number, revisionNumber: number, stopRecno: number, payload: StopoffFieldsPayload): Promise<{ recno: number }> {
    const response = await api.patch(`/quotes/${quoteNumber}/${revisionNumber}/stopoffs/${stopRecno}`, payload);
    return response.data as { recno: number };
  },

  /** DELETE /quotes/{q}/{r}/stopoffs/{recno} — soft-delete a stopoff. */
  async deleteStopoff(quoteNumber: number, revisionNumber: number, stopRecno: number): Promise<{ recno: number }> {
    const response = await api.delete(`/quotes/${quoteNumber}/${revisionNumber}/stopoffs/${stopRecno}`);
    return response.data as { recno: number };
  },

  /** GET /quotes/rate-standards — standard/minimum rate lookups for the
   *  below-minimum warnings (V1 getDrayStandards et al.). */
  async rateStandards(): Promise<RateStandards> {
    const response = await api.get('/quotes/rate-standards');
    return response.data as RateStandards;
  },

  /** GET /quotes/{q}/{r}/pdf — the customer-facing quote PDF as a Blob
   *  (V1 'View Quote'). Open via URL.createObjectURL in a new tab. `extraQr`
   *  (#36) appends more 'q-r' refs into one combined PDF (N23 multi-quote). */
  async quotePdf(quoteNumber: number, revisionNumber: number, extraQr: string[] = []): Promise<Blob> {
    const response = await api.get(`/quotes/${quoteNumber}/${revisionNumber}/pdf`, {
      responseType: 'blob',
      params: extraQr.length ? { qr: extraQr.join(',') } : undefined,
    });
    return response.data as Blob;
  },

  /** POST /quotes/{q}/{r}/send — email the quote PDF (V1 View/Send Quote).
   *  Outside production the backend redirects to SENDGRID_TEST_RECIPIENT. */
  async sendQuote(
    quoteNumber: number,
    revisionNumber: number,
    payload: SendQuoteRecipients,
  ): Promise<{ sent: boolean; recipients: string[] }> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/send`, payload);
    return response.data as { sent: boolean; recipients: string[] };
  },

  /** POST /quotes/send-multi (#36) — email ONE combined PDF of the selected
   *  quote revisions ("q-r", a leading "Q-" is tolerated). */
  async sendQuotesMulti(
    quoteRevisions: string[],
    payload: SendQuoteRecipients,
  ): Promise<{ sent: boolean; recipients: string[] }> {
    const response = await api.post('/quotes/send-multi', { ...payload, quoteRevisions });
    return response.data as { sent: boolean; recipients: string[] };
  },

  /** GET the selectable EXTRA-IN-RATE combos for a revision (N39) — the
   *  powerset per scope, with saved selections pre-checked. */
  async getExtraInRateCombos(quoteNumber: number, revisionNumber: number): Promise<ExtraInRateScope[]> {
    const response = await api.get(`/quotes/${quoteNumber}/${revisionNumber}/extra-in-rate-combos`);
    return response.data as ExtraInRateScope[];
  },

  /** Replace a revision's saved EXTRA-IN-RATE combos (N39). */
  async putExtraInRateCombos(
    quoteNumber: number,
    revisionNumber: number,
    combos: Array<{ tablename: string; tablerecno: number; recnolist: string }>,
  ): Promise<void> {
    await api.put(`/quotes/${quoteNumber}/${revisionNumber}/extra-in-rate-combos`, { combos });
  },

  /** GET /quotes/typeahead — quote-number prefix search for the PRO Quote #
   *  picker. One row per non-deleted revision, so quote+revision is one pick.
   *  Fewer than 2 digits returns []. */
  async searchQuotesForPicker(q: string, limit?: number): Promise<QuotePickerRow[]> {
    const response = await api.get('/quotes/typeahead', { params: { q, ...(limit ? { limit } : {}) } });
    return response.data as QuotePickerRow[];
  },

  /** GET /quotes/lookup-for-pro — the spyglass. Suggests the quote(s) matching a
   *  PRO's bill-to / customer / rate zone / delivery location (V1 quotelookup
   *  DIRECT arm). `rows` may be empty with a `message` explaining why.
   *
   *  `context` carries the OPEN FORM's values, which V1 did too — its quotevars
   *  string was assembled entirely from editrecordform (js/cms.js:2950-2998), so
   *  the spyglass matched against unsaved edits. Each supplied field overrides the
   *  saved record server-side; omit it and every field falls back to the DB. */
  async lookupQuoteForPro(
    pronumber: number | string,
    context?: Partial<QuoteLookupContext> | null,
  ): Promise<QuotePickerLookupResult> {
    const response = await api.get('/quotes/lookup-for-pro', {
      params: { pronumber, ...(context ?? {}) },
    });
    return response.data as QuotePickerLookupResult;
  },

  /** GET /quotes/productcode-lookup — product codes for a warehouse customer. */
  async productCodeLookup(customercode: number): Promise<Array<{ recno: number; sku: string; description: string; masterUom: string }>> {
    const response = await api.get('/quotes/productcode-lookup', { params: { customercode } });
    return response.data as Array<{ recno: number; sku: string; description: string; masterUom: string }>;
  },

  /** POST /quotes/{q}/{r}/product-rates — add a per-SKU rate (WAREHOUSE). */
  async addProductRate(quoteNumber: number, revisionNumber: number, payload: ProductRateFieldsPayload): Promise<{ recno: number }> {
    const response = await api.post(`/quotes/${quoteNumber}/${revisionNumber}/product-rates`, payload);
    return response.data as { recno: number };
  },

  /** PATCH /quotes/{q}/{r}/product-rates/{recno} — edit a per-SKU rate. */
  async updateProductRate(quoteNumber: number, revisionNumber: number, rateRecno: number, payload: ProductRateFieldsPayload): Promise<{ recno: number }> {
    const response = await api.patch(`/quotes/${quoteNumber}/${revisionNumber}/product-rates/${rateRecno}`, payload);
    return response.data as { recno: number };
  },

  /** DELETE /quotes/{q}/{r}/product-rates/{recno} — soft-delete a per-SKU rate. */
  async deleteProductRate(quoteNumber: number, revisionNumber: number, rateRecno: number): Promise<{ recno: number }> {
    const response = await api.delete(`/quotes/${quoteNumber}/${revisionNumber}/product-rates/${rateRecno}`);
    return response.data as { recno: number };
  },
};

export default quoteService;
